import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LeadForm({ h, onDone }) {
  const [f, setF] = useState({ name: "", phone: "", city: "", travel_date: "" });
  const add = async () => {
    try { const r = await axios.post(`${API}/admin/homestay/leads`, { leads: [{ ...f, city: f.city || null, travel_date: f.travel_date || null }] }, h); toast.success(r.data.added ? "Lead added" : "Duplicate or invalid phone"); setF({ name: "", phone: "", city: "", travel_date: "" }); onDone(); }
    catch { toast.error("Enter name and a 10-digit phone"); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await axios.post(`${API}/admin/homestay/leads/csv`, fd, h); toast.success(`${r.data.added} added · ${r.data.skipped} skipped`); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "CSV upload failed"); }
    e.target.value = "";
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mt-2 flex flex-wrap gap-2 items-center">
      <input data-testid="admin-lead-name" placeholder="Name" value={f.name} onChange={set("name")} className="rounded-xl border px-2 py-1.5 text-xs" />
      <input data-testid="admin-lead-phone" placeholder="Phone" value={f.phone} onChange={set("phone")} className="rounded-xl border px-2 py-1.5 text-xs w-32" />
      <input data-testid="admin-lead-city" placeholder="City" value={f.city} onChange={set("city")} className="rounded-xl border px-2 py-1.5 text-xs w-24" />
      <input data-testid="admin-lead-date" type="date" value={f.travel_date} onChange={set("travel_date")} className="rounded-xl border px-2 py-1.5 text-xs" />
      <button data-testid="admin-lead-add" onClick={add} className="chip text-xs" style={{ color: BRAND.green }}>+ Add lead</button>
      <label className="chip text-xs cursor-pointer" style={{ color: BRAND.orange }}>Upload CSV<input data-testid="admin-lead-csv" type="file" accept=".csv" className="hidden" onChange={upload} /></label>
      <span className="text-[10px]" style={{ color: BRAND.muted }}>CSV columns: name, phone, city, travel_date, note</span>
    </div>
  );
}

function AssignForm({ h, props, price, onDone }) {
  const [f, setF] = useState({ property_id: "", price_inr: "", free: false });
  const assign = async () => {
    try { await axios.post(`${API}/admin/homestay/lead-batches`, { property_id: f.property_id, free: f.free, price_inr: f.price_inr === "" ? null : +f.price_inr }, h); toast.success("Batch of 10 assigned"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      <select data-testid="admin-batch-property" value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })} className="rounded-xl border px-2 py-1.5 text-xs">
        <option value="">Pick property…</option>{props.filter((p) => p.status === "approved").map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <input data-testid="admin-batch-price" type="number" min={0} placeholder={`₹${price}`} disabled={f.free} value={f.price_inr} onChange={(e) => setF({ ...f, price_inr: e.target.value })} className="rounded-xl border px-2 py-1.5 text-xs w-24" />
      <label className="text-xs inline-flex items-center gap-1"><input data-testid="admin-batch-free" type="checkbox" checked={f.free} onChange={(e) => setF({ ...f, free: e.target.checked })} /> Free unlock</label>
      <button data-testid="admin-batch-assign" disabled={!f.property_id} onClick={assign} className="chip text-xs disabled:opacity-50" style={{ color: BRAND.green }}>Assign batch of 10</button>
    </div>
  );
}

export function AdminLeads({ token, properties }) {
  const [d, setD] = useState({ pool: 0, batches: [], default_price: 99 });
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(`${API}/admin/homestay/leads`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setD(r.data)).catch(() => {}), [token]);
  useEffect(() => { load(); }, [load]);
  const unlock = async (id, free) => {
    try { await axios.post(`${API}/admin/homestay/lead-batches/${id}/unlock${free ? "?free=true" : ""}`, {}, h); toast.success(free ? "Unlocked for free" : "Payment confirmed · unlocked"); load(); }
    catch { toast.error("Unlock failed"); }
  };
  return (
    <div data-testid="admin-hs-leads" className="rounded-2xl border bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="font-bold text-sm" style={{ color: BRAND.green }}>Contact leads · <span data-testid="admin-lead-pool">{d.pool}</span> in pool · default ₹{d.default_price}/batch</div>
      <LeadForm h={h} onDone={load} />
      <AssignForm h={h} props={properties} price={d.default_price} onDone={load} />
      <div className="mt-3 max-h-72 overflow-y-auto">
        {d.batches.map((b) => (
          <div key={b.id} data-testid={`admin-batch-${b.id}`} className="flex items-center justify-between py-2 border-t text-sm" style={{ borderColor: BRAND.line }}>
            <span><b>{b.property_name}</b> <span className="text-xs" style={{ color: BRAND.muted }}>Batch #{b.batch_no} · {b.lead_ids.length} leads · ₹{b.price_inr}</span></span>
            {b.status === "locked" ? (
              <span className="flex gap-2">
                <button data-testid={`admin-batch-confirm-${b.id}`} onClick={() => unlock(b.id, false)} className="chip text-xs" style={{ color: BRAND.green }}>✓ Confirm paid</button>
                <button data-testid={`admin-batch-free-${b.id}`} onClick={() => unlock(b.id, true)} className="chip text-xs" style={{ color: BRAND.orange }}>Mark free</button>
              </span>
            ) : <span className="text-xs font-bold" style={{ color: BRAND.green }} data-testid={`admin-batch-status-${b.id}`}>unlocked</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
